import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, isDevMode } from '@angular/core';
import { Observable, catchError, finalize, shareReplay, switchMap, throwError } from 'rxjs';

import { AuthService } from './auth.service';
import { AuthToken } from '../models/user.model';
import { isAccessTokenExpired } from './jwt.util';

const RETRY_HEADER = 'X-Auth-Retry';

// Public auth endpoints — no Bearer attached, no refresh-on-401.
// /api/auth/me is intentionally NOT here: it is protected and must follow the
// normal Bearer + refresh flow (otherwise reload always logs the user out).
const PUBLIC_AUTH_PATHS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/refresh',
  '/api/auth/logout',
  '/api/auth/google',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/auth/verify-email',
  '/api/auth/resend-verification',
  '/api/auth/providers',
];

let refreshInProgress$: Observable<AuthToken> | null = null;

/**
 * Test-only helper: clears the module-level refresh lock between specs.
 * Guarded by isDevMode so production builds tree-shake it.
 */
export function __resetAuthInterceptorLockForTests(): void {
  if (!isDevMode()) return;
  refreshInProgress$ = null;
}

export const authInterceptorFn: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);

  if (isPublicAuthRoute(req.url)) {
    return next(req);
  }

  const token = auth.getAccessToken();

  // Refresh PROACTIVO: si ya sabemos que el access token está caducado (o lo estará en
  // segundos), renovamos UNA vez ANTES de mandar la petición. Así una tanda de peticiones
  // al arrancar la app no sale toda con un token muerto provocando una oleada de 401 en
  // paralelo (que además abría la puerta a varios /refresh). Todas comparten el mismo lock,
  // esperan al único refresh y salen ya con el token nuevo.
  if (token && auth.getRefreshToken() && isAccessTokenExpired(token)) {
    return ensureRefresh(auth).pipe(
      switchMap(() => sendWithReactiveRefresh(req, next, auth))
    );
  }

  return sendWithReactiveRefresh(req, next, auth);
};

/**
 * Manda la petición con el token actual y, si el backend responde 401, hace un refresh
 * (compartido) y reintenta UNA sola vez. La segunda vez lleva la cabecera de reintento,
 * de modo que un 401 en el reintento cierra la sesión en vez de reintentar en bucle.
 */
function sendWithReactiveRefresh(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthService
): Observable<HttpEvent<unknown>> {
  return next(attachToken(req, auth.getAccessToken())).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      // Avoid infinite loop: if this request is already a retry, give up.
      if (req.headers.has(RETRY_HEADER)) {
        auth.clearSession();
        return throwError(() => error);
      }

      return ensureRefresh(auth).pipe(
        switchMap(() =>
          next(attachToken(req.clone({ setHeaders: { [RETRY_HEADER]: '1' } }), auth.getAccessToken()))
        )
      );
    })
  );
}

/**
 * Refresh compartido con lock a nivel de módulo: aunque varias peticiones fallen con 401
 * a la vez (o varias detecten el token caducado por el refresh proactivo), solo se dispara
 * UN `/refresh` y todas esperan su resultado. Si el refresh falla, limpia la sesión.
 */
function ensureRefresh(auth: AuthService): Observable<AuthToken> {
  if (!refreshInProgress$) {
    refreshInProgress$ = auth.refreshTokens().pipe(
      catchError(refreshError => {
        auth.clearSession();
        return throwError(() => refreshError);
      }),
      finalize(() => {
        refreshInProgress$ = null;
      }),
      shareReplay(1)
    );
  }
  return refreshInProgress$;
}

function isPublicAuthRoute(url: string): boolean {
  return PUBLIC_AUTH_PATHS.some(path => url.includes(path));
}

function attachToken<T>(req: HttpRequest<T>, token: string | null): HttpRequest<T> {
  if (!token) return req;
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}
